// W-96c0e692: a time the way a person states it, a local date and time in a
// named IANA zone, and the instant it names, converted in code so no model
// does epoch arithmetic. The shape follows the established calendar APIs:
// Google Calendar's EventDateTime and Microsoft Graph's dateTimeTimeZone, both
// { dateTime, timeZone } with the zone naming how a dateTime without an
// offset is read. Unlike them, dateTime here never carries an offset or "Z":
// the zone alone says how it is read, so the two can never disagree.
// Dependency-free: the World (backend/convex/lib/wallClock.js), the Desk and
// the web app share it.

export interface WallClock {
  readonly dateTime: string;
  readonly timeZone: string;
}
export interface WallClockReading extends WallClock {
  readonly weekday: string;
  /** The zone's offset from UTC at that instant, "-04:00": tells apart the two readings of a repeated hour. */
  readonly utcOffset: string;
}
export type InstantOutcome =
  | { readonly ok: true; readonly instant: number }
  | { readonly ok: false; readonly reason: string };

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{3}))?)?$/u;
const OFFSET = /^([+-])(\d{2}):(\d{2})$/u;
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function validTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string" || timeZone === "" || timeZone.length > 128) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

const pad = (n: number, width = 2): string => String(n).padStart(width, "0");
const formatters = new Map<string, Intl.DateTimeFormat>();
interface Wall {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}
function wallParts(instant: number, timeZone: string): Wall {
  let format = formatters.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, format);
  }
  const parts: Record<string, number> = {};
  for (const part of format.formatToParts(instant))
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  return {
    year: parts["year"]!,
    month: parts["month"]!,
    day: parts["day"]!,
    hour: parts["hour"]!,
    minute: parts["minute"]!,
    second: parts["second"]!,
  };
}
const floating = (wall: Wall): number =>
  Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
const DAY = 86_400_000;

const offsetText = (minutes: number): string =>
  `${minutes < 0 ? "-" : "+"}${pad(Math.floor(Math.abs(minutes) / 60))}:${pad(Math.abs(minutes) % 60)}`;

/**
 * An instant as the wall clock in a zone reads it: the same shape a write
 * takes, plus its weekday and UTC offset. Passed back as read, it names the
 * same instant: seconds and milliseconds are kept when not zero, and the
 * offset tells apart the two readings of an hour the clocks repeat.
 */
export function wallClockOf(instant: number, timeZone: string): WallClockReading {
  const millis = ((instant % 1000) + 1000) % 1000;
  const wall = wallParts(instant - millis, timeZone);
  // 1 January 1970 was a Thursday.
  const weekday =
    WEEKDAYS[
      (((Math.floor(Date.UTC(wall.year, wall.month - 1, wall.day) / DAY) + 4) % 7) + 7) % 7
    ]!;
  const seconds =
    wall.second || millis ? `:${pad(wall.second)}${millis ? `.${pad(millis, 3)}` : ""}` : "";
  return {
    dateTime: `${pad(wall.year, 4)}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}${seconds}`,
    timeZone,
    weekday,
    utcOffset: offsetText(Math.round((floating(wall) - (instant - millis)) / 60_000)),
  };
}

/** Every instant the zone's clock reads as `wall`: none in a skipped hour, two in a repeated one. */
function instantsOf(wall: Wall, timeZone: string): number[] {
  const at = floating(wall);
  const offsets = new Set(
    [at - DAY, at, at + DAY].map((probe) => floating(wallParts(probe, timeZone)) - probe),
  );
  return [...offsets]
    .map((offset) => at - offset)
    .filter((instant) => floating(wallParts(instant, timeZone)) === at)
    .sort((a, b) => a - b);
}

/**
 * A time field's value as an instant: a number passes through; { dateTime,
 * timeZone } is converted. A local time that does not exist in the zone (the
 * hour skipped when clocks go forward) is refused rather than moved; one that
 * occurs twice (clocks go back) is its earlier occurrence, as RFC 5545 reads
 * it, unless utcOffset names the later one.
 */
export function instantFrom(value: unknown, field: string): InstantOutcome {
  if (typeof value === "number") return { ok: true, instant: value };
  if (!value || typeof value !== "object" || Array.isArray(value))
    return { ok: false, reason: `${field} must be epoch milliseconds or { dateTime, timeZone }` };
  // A time read back (wallClockOf) also names its weekday and offset; it may be passed back as read.
  const extra = Object.keys(value).find(
    (key) => !["dateTime", "timeZone", "weekday", "utcOffset"].includes(key),
  );
  if (extra !== undefined)
    return { ok: false, reason: `${field} may not carry '${extra}'; give { dateTime, timeZone }` };
  const { dateTime, timeZone, weekday, utcOffset } = value as {
    dateTime?: unknown;
    timeZone?: unknown;
    weekday?: unknown;
    utcOffset?: unknown;
  };
  const offset = typeof utcOffset === "string" ? OFFSET.exec(utcOffset) : null;
  if (utcOffset !== undefined && offset === null)
    return { ok: false, reason: `${field}.utcOffset must look like -04:00` };
  if (!validTimeZone(timeZone))
    return {
      ok: false,
      reason: `${field}.timeZone must be an IANA time zone such as America/New_York`,
    };
  const match = typeof dateTime === "string" ? LOCAL.exec(dateTime) : null;
  if (!match)
    return {
      ok: false,
      reason: `${field}.dateTime must be a local date and time without an offset, such as 2026-10-21T09:00; the zone goes in timeZone`,
    };
  const [year = 0, month = 0, day = 0, hour = 0, minute = 0, second = 0, millis = 0] = match
    .slice(1)
    .map((part) => (part === undefined ? 0 : Number(part)));
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 0;
  if (year < 1970 || day < 1 || day > days || hour > 23 || minute > 59 || second > 59)
    return { ok: false, reason: `${field}.dateTime ${dateTime} is not a real date and time` };
  const wall = { year, month, day, hour, minute, second };
  const readings = instantsOf(wall, timeZone);
  if (readings.length === 0)
    return {
      ok: false,
      reason: `${field}: ${dateTime} does not exist in ${timeZone} (the clocks change then); choose another time`,
    };
  // A repeated hour is its earlier reading unless the offset names the later one.
  const offsetMinutes = offset
    ? (offset[1] === "-" ? -1 : 1) * (Number(offset[2]) * 60 + Number(offset[3]))
    : null;
  const chosen =
    offsetMinutes === null
      ? readings[0]
      : readings.find((at) => (floating(wall) - at) / 60_000 === offsetMinutes);
  if (chosen === undefined)
    return {
      ok: false,
      reason: `${field}: ${dateTime} is not at ${String(utcOffset)} in ${timeZone}`,
    };
  const instant = chosen + millis;
  // A weekday given with the time must be that date's: a mismatch means the day was misread.
  if (weekday !== undefined && weekday !== wallClockOf(instant, timeZone).weekday)
    return {
      ok: false,
      reason: `${field}: ${dateTime} is a ${wallClockOf(instant, timeZone).weekday}, not ${String(weekday)}`,
    };
  return { ok: true, instant };
}
